// Kho mô phỏng theo BÀI (tra cứu deterministic — KHÔNG dùng AI).
// Mỗi mục = 1 mô phỏng gắn với (lop, chuong, bai). getSimsByLesson() gộp:
//  - mô phỏng canvas CHUYÊN BIỆT đã đăng ký trong SIM_REGISTRY (vd interactive_oscillation),
//  - mô phỏng GeoGebra theo bài (GeoGebraService),
//  - (có thể thêm video sau).
// Khi người dùng chọn bài → hiển thị danh sách mô phỏng phù hợp để xem/chèn vào bài giảng.
import { GeoGebraService, type GeoGebraContent } from '../video/GeoGebraService'
import { LabService, type LabExperiment } from '../lab/LabService'

export type SimKind = 'canvas' | 'geogebra' | 'video' | 'lab'

export interface SimEntry {
  id: string
  /** type dùng cho InteractiveContentRenderer / block nội dung */
  type: string
  kind: SimKind
  title: string
  description?: string
  lop: number
  chuong: number
  bai: number
  ggbFile?: string     // kind = 'geogebra'
  videoPath?: string   // kind = 'video'
  url?: string         // kind = 'lab' (link đầy đủ sang lab.vatli365.vn)
  params?: Record<string, any>
}

// Mô phỏng canvas CHUYÊN BIỆT đã có component render (đăng ký thêm khi viết component mới).
const SIM_REGISTRY: SimEntry[] = [
  {
    id: 'sim-l11-c1-b1-oscillation',
    type: 'interactive_oscillation',
    kind: 'canvas',
    title: 'Mô phỏng Dao động điều hòa',
    description: 'Vật dao động quanh VTCB + đồ thị x(t); chỉnh biên độ A, tần số f, pha φ.',
    lop: 11, chuong: 1, bai: 1,
  },
  {
    id: 'sim-l11-c1-b2-springosc',
    type: 'interactive_spring_oscillator',
    kind: 'canvas',
    title: 'Con lắc lò xo — kéo & thả (tắt dần)',
    description: 'Kéo vật tới x₀ rồi thả: biên độ = x₀. Cấp v₀ → biên độ lớn hơn. Tăng hệ số cản → tắt dần. Đồ thị x(t).',
    lop: 11, chuong: 1, bai: 2,
  },
  {
    id: 'sim-l11-c1-b3-pendulum',
    type: 'interactive_simple_pendulum',
    kind: 'canvas',
    title: 'Con lắc đơn — kéo & thả (tắt dần)',
    description: 'Kéo tới góc θ₀ rồi thả: biên độ góc = θ₀. Cấp v₀ → biên độ lớn hơn. Tăng cản → tắt dần. T=2π√(l/g). Đồ thị θ(t).',
    lop: 11, chuong: 1, bai: 3,
  },
  {
    id: 'sim-l11-c1-b1-twoosc',
    type: 'interactive_two_oscillations',
    kind: 'canvas',
    title: 'Hai dao động điều hoà — độ lệch pha',
    description: 'Hai dao động cùng tần số; chỉnh biên độ & pha ban đầu (nhập radian, vd pi/2) để thấy độ lệch pha Δφ.',
    lop: 11, chuong: 1, bai: 1,
  },
  {
    id: 'sim-l10-c1-b4-vtgraph',
    type: 'interactive_vt_graph',
    kind: 'canvas',
    title: 'Đồ thị độ dịch chuyển – thời gian',
    description: 'Tương tác đồ thị v–t / d–t cho chuyển động.',
    lop: 10, chuong: 1, bai: 4,
  },
  {
    id: 'sim-l12-c4-b21-radioactive',
    type: 'interactive_radioactive_decay',
    kind: 'canvas',
    title: 'Mô phỏng Phóng xạ N(t)=N₀·2^(−t/T)',
    description: 'Phân rã hạt nhân ngẫu nhiên + đường cong số hạt còn lại theo chu kì bán rã.',
    lop: 12, chuong: 4, bai: 21,
  },
  {
    id: 'sim-l12-c2-b11-gas',
    type: 'interactive_gas_particles',
    kind: 'canvas',
    title: 'Mô hình động học phân tử chất khí',
    description: 'Phân tử khí va chạm thành bình → áp suất; nhiệt độ ↑ → tốc độ & động năng ↑.',
    lop: 12, chuong: 2, bai: 11,
  },
  {
    id: 'sim-l12-c2-b7-gas',
    type: 'interactive_gas_particles',
    kind: 'canvas',
    title: 'Chuyển động phân tử khí (mô hình động học)',
    description: 'Quan sát chuyển động hỗn loạn của phân tử khí.',
    lop: 12, chuong: 2, bai: 7,
  },
  {
    id: 'sim-l12-c1-b1-phase',
    type: 'interactive_phase_change',
    kind: 'canvas',
    title: 'Sự chuyển thể: rắn → lỏng → khí',
    description: 'Tăng nhiệt độ để quan sát chuyển thể của chất.',
    lop: 12, chuong: 1, bai: 1,
  },
  {
    id: 'sim-l12-c4-b20-fission',
    type: 'interactive_nuclear_reaction',
    kind: 'canvas',
    title: 'Phản ứng phân hạch dây chuyền',
    description: 'Bắn neutron vào U-235 → vỡ + phát neutron → phản ứng dây chuyền.',
    lop: 12, chuong: 4, bai: 20,
  },
  {
    id: 'sim-l11-c2-b5-wave',
    type: 'interactive_wave',
    kind: 'canvas',
    title: 'Mô tả và sự truyền sóng',
    description: 'Sóng ngang lan truyền; quan sát λ, T, v = λ·f.',
    lop: 11, chuong: 2, bai: 5,
  },
  {
    id: 'sim-l12-c3-b15-acgen',
    type: 'interactive_ac_generator',
    kind: 'canvas',
    title: 'Máy phát điện xoay chiều (khung quay → e=E₀sinωt)',
    description: 'Khung dây quay trong từ trường tạo suất điện động xoay chiều; đồng bộ khung quay & đồ thị.',
    lop: 12, chuong: 3, bai: 15,
  },
  {
    id: 'sim-l12-c1-b4-specheat',
    type: 'interactive_specific_heat',
    kind: 'canvas',
    title: 'Nhiệt dung riêng — Q = m·c·ΔT',
    description: 'Tính nhiệt lượng làm nóng vật; so sánh các chất có nhiệt dung riêng khác nhau.',
    lop: 12, chuong: 1, bai: 4,
  },
  {
    id: 'sim-l12-c1-b5-melting',
    type: 'interactive_heating_curve',
    kind: 'canvas',
    title: 'Đường cong nhiệt độ – nhiệt lượng (nóng chảy)',
    description: 'Quan sát đoạn nằm ngang khi nóng chảy (nhiệt nóng chảy riêng).',
    lop: 12, chuong: 1, bai: 5,
  },
  {
    id: 'sim-l12-c1-b6-vapor',
    type: 'interactive_heating_curve',
    kind: 'canvas',
    title: 'Đường cong nhiệt độ – nhiệt lượng (hoá hơi)',
    description: 'Quan sát đoạn nằm ngang khi sôi/hoá hơi (nhiệt hoá hơi riêng).',
    lop: 12, chuong: 1, bai: 6,
  },
  {
    id: 'sim-l11-c3-b11-coulomb',
    type: 'interactive_coulomb',
    kind: 'canvas',
    title: 'Lực tương tác Coulomb',
    description: 'Kéo 2 điện tích, đổi dấu/độ lớn → vector lực hút/đẩy, F = k·|q₁q₂|/r².',
    lop: 11, chuong: 3, bai: 11,
  },
  {
    id: 'sim-l11-c1-b3-energy',
    type: 'interactive_energy_oscillation',
    kind: 'canvas',
    title: 'Năng lượng trong dao động điều hoà',
    description: 'Chuyển hoá Wđ ↔ Wt, cơ năng W không đổi (cột năng lượng).',
    lop: 11, chuong: 1, bai: 3,
  },
  {
    id: 'sim-l11-c3-b12-efield',
    type: 'interactive_electric_field',
    kind: 'canvas',
    title: 'Điện trường đều giữa hai bản',
    description: 'Đường sức song song; điện tích thử chịu lực F=qE và chuyển động có gia tốc.',
    lop: 11, chuong: 3, bai: 12,
  },
  {
    id: 'sim-l10-c3-b16-collision',
    type: 'interactive_collision',
    kind: 'canvas',
    title: 'Va chạm & bảo toàn động lượng',
    description: 'Hai vật va chạm (đàn hồi ↔ mềm) — động lượng bảo toàn.',
    lop: 10, chuong: 3, bai: 16,
  },
  {
    id: 'sim-l10-c3-b17-collision',
    type: 'interactive_collision',
    kind: 'canvas',
    title: 'Động lượng và va chạm',
    description: 'Va chạm đàn hồi/mềm với hệ số phục hồi e.',
    lop: 10, chuong: 3, bai: 17,
  },
  {
    id: 'sim-l10-c4-b20-hooke',
    type: 'interactive_hooke',
    kind: 'canvas',
    title: 'Định luật Hooke — F = k·Δl',
    description: 'Lực đàn hồi tỉ lệ độ giãn; đồ thị F–Δl là đường thẳng (độ dốc = k).',
    lop: 10, chuong: 4, bai: 20,
  },
  {
    id: 'sim-l10-c2-b13-incline',
    type: 'interactive_inclined_plane',
    kind: 'canvas',
    title: 'Mặt phẳng nghiêng — phân tích lực & ma sát',
    description: 'Kéo vật lên dốc; phân tích P thành Pₜ (//) và Pₙ (⊥); ma sát Fₘₛ=μN. Vật tự trượt khi Pₜ>Fₘₛ hoặc cho v₀ để chuyển động nhanh dần đều.',
    lop: 10, chuong: 2, bai: 13,
  },
  {
    id: 'sim-l10-c2-b10-forcevec',
    type: 'interactive_force_vectors',
    kind: 'canvas',
    title: 'Tổng hợp & phân tích lực',
    description: 'Kéo vec-tơ lực: tổng hợp 2 lực theo quy tắc hình bình hành; hoặc phân tích 1 lực thành 2 thành phần theo trục (xoay được).',
    lop: 10, chuong: 2, bai: 10,
  },
  {
    id: 'sim-l12-c3-b13-magforce',
    type: 'interactive_magnetic_force',
    kind: 'canvas',
    title: 'Lực từ tác dụng lên dây dẫn (3D)',
    description: 'F = B·I·L vuông góc với I và B (quy tắc bàn tay trái) — mô hình 3D xoay được.',
    lop: 12, chuong: 3, bai: 13,
  },
  {
    id: 'sim-l12-c4-b18-nucleus',
    type: 'interactive_nucleus_structure',
    kind: 'canvas',
    title: 'Cấu trúc hạt nhân (3D)',
    description: 'Z proton (đỏ) + N neutron (xanh), số khối A = Z + N — khối hạt nhân 3D xoay được.',
    lop: 12, chuong: 4, bai: 18,
  },
  {
    id: 'sim-l10-c4-b18-circular',
    type: 'interactive_circular_motion',
    kind: 'canvas',
    title: 'Chuyển động tròn đều (v, a hướng tâm)',
    description: 'Vận tốc tiếp tuyến, gia tốc hướng tâm a=ω²r; chu kì T=2π/ω.',
    lop: 10, chuong: 4, bai: 18,
  },
  {
    id: 'sim-l10-c4-b19-circular',
    type: 'interactive_circular_motion',
    kind: 'canvas',
    title: 'Gia tốc & lực hướng tâm',
    description: 'a = ω²r = v²/r hướng vào tâm; lực hướng tâm F = m·a.',
    lop: 10, chuong: 4, bai: 19,
  },
  {
    id: 'sim-l12-c2-b9-charles',
    type: 'interactive_gas_law',
    kind: 'canvas',
    title: 'Định luật Charles (V ∝ T)',
    description: 'Giữ áp suất, tăng nhiệt độ → thể tích tăng; pV/T không đổi.',
    lop: 12, chuong: 2, bai: 9,
  },
  {
    id: 'sim-l12-c2-b10-gaslaw',
    type: 'interactive_gas_law',
    kind: 'canvas',
    title: 'Phương trình trạng thái khí lí tưởng',
    description: 'pV/T = hằng số — chỉnh p, T để quan sát thể tích & pittông.',
    lop: 12, chuong: 2, bai: 10,
  },
  {
    id: 'sim-l12-c3-b12-magfield',
    type: 'interactive_magnetic_field',
    kind: 'canvas',
    title: 'Từ trường của nam châm (3D)',
    description: 'Đường sức từ đi ra cực N, vòng về cực S — mô hình 3D xoay được.',
    lop: 12, chuong: 3, bai: 12,
  },
  {
    id: 'sim-l11-c1-b4-resonance',
    type: 'interactive_damped_resonance',
    kind: 'canvas',
    title: 'Dao động cưỡng bức & cộng hưởng',
    description: 'Đường cong cộng hưởng A(ω); ω → ω₀ → biên độ cực đại; ma sát ảnh hưởng độ cao/độ tù của đỉnh.',
    lop: 11, chuong: 1, bai: 4,
  },
  {
    id: 'sim-l11-c2-b8-interference',
    type: 'interactive_wave_interference',
    kind: 'canvas',
    title: 'Giao thoa sóng hai nguồn',
    description: 'Vân cực đại/cực tiểu của 2 nguồn cùng pha; |r₁−r₂| = mλ (cực đại) / (m+½)λ (cực tiểu).',
    lop: 11, chuong: 2, bai: 8,
  },
  {
    id: 'sim-l11-c2-b9-standingwave',
    type: 'interactive_standing_wave',
    kind: 'canvas',
    title: 'Sóng dừng trên dây hai đầu cố định',
    description: 'Nút & bụng sóng; L = n·λ/2; số bụng = n, số nút = n+1.',
    lop: 11, chuong: 2, bai: 9,
  },
  {
    id: 'sim-l10-c1-b4-disptime',
    type: 'interactive_displacement_time',
    kind: 'canvas',
    title: 'Đồ thị độ dịch chuyển – thời gian',
    description: 'Chuyển động thẳng đều: đồ thị d–t là đường thẳng (độ dốc = vận tốc). Đổi vận tốc khi chạy → đổi độ dốc.',
    lop: 10, chuong: 1, bai: 4,
  },
  {
    id: 'sim-l10-c1-b5-motiongraphs',
    type: 'interactive_motion_graphs',
    kind: 'canvas',
    title: 'Chuyển động biến đổi: đồ thị d, v, a',
    description: 'Chuyển động biến đổi đều: bật/tắt đồ thị độ dịch chuyển, vận tốc, gia tốc theo thời gian.',
    lop: 10, chuong: 1, bai: 5,
    params: { mode: 'accelerated' },
  },
  {
    id: 'sim-l10-c1-b6-freefall3d',
    type: 'interactive_freefall_3d',
    kind: 'canvas',
    title: 'Rơi tự do (3D)',
    description: 'Vật rơi 3D (nền đất, bầu trời) + đồ thị d, v, a (a = g = 9,8 m/s²); bật/tắt từng đồ thị.',
    lop: 10, chuong: 1, bai: 6,
  },
  {
    id: 'sim-l10-c1-b7-projectile',
    type: 'interactive_projectile_motion',
    kind: 'canvas',
    title: 'Chuyển động ném (ngang & xiên)',
    description: 'Ném ngang/ném xiên (đổi góc α); quỹ đạo parabol + đồ thị toạ độ x(t), y(t).',
    lop: 10, chuong: 1, bai: 7,
  },
  {
    id: 'sim-l11-c3-b17-circuit',
    type: 'interactive_circuit',
    kind: 'canvas',
    title: 'Nguồn điện và mạch điện kín',
    description: 'Đóng/mở công tắc; dòng điện chạy từ nguồn qua bóng đèn; đèn sáng khi mạch kín.',
    lop: 11, chuong: 3, bai: 17,
  },
  {
    id: 'sim-l11-c3-b16-resistance',
    type: 'interactive_resistance_ohm',
    kind: 'canvas',
    title: 'Điện trở & định luật Ohm (3 đồ thị)',
    description: 'I–U điện trở không đổi; R–T đèn sợi đốt (tăng); R–T của NTC (giảm).',
    lop: 11, chuong: 3, bai: 16,
  },
  {
    id: 'sim-l11-c3-b17-emf',
    type: 'interactive_emf_measurement',
    kind: 'canvas',
    title: 'Đo suất điện động & điện trở trong (đồ thị I–U)',
    description: 'U = E − I·r; giao trục U = E, độ dốc = −r.',
    lop: 11, chuong: 3, bai: 17,
  },
  {
    id: 'sim-l12-c1-b6-vaporization',
    type: 'interactive_vaporization',
    kind: 'canvas',
    title: 'Quá trình hoá hơi (nhiệt hoá hơi riêng)',
    description: 'Đun nước: tới 100°C nhiệt độ giữ nguyên trong lúc hoá hơi; Q = L·m.',
    lop: 12, chuong: 1, bai: 6,
  },
  {
    id: 'sim-l12-c3-b14-induction',
    type: 'interactive_em_induction',
    kind: 'canvas',
    title: 'Từ thông & cảm ứng điện từ',
    description: 'Nam châm chuyển động → Φ biến thiên → suất điện động & dòng điện cảm ứng (kim điện kế lệch).',
    lop: 12, chuong: 3, bai: 14,
  },
]

function ggbToEntry(g: GeoGebraContent): SimEntry {
  return {
    id: `ggb-${g.filename}`,
    type: 'simulation',
    kind: 'geogebra',
    title: g.title,
    description: g.description,
    lop: g.lop, chuong: g.chuong, bai: g.bai,
    ggbFile: g.filename,
  }
}

function labToEntry(e: LabExperiment): SimEntry {
  return {
    id: `lab-${e.id}`,
    type: 'lab',
    kind: 'lab',
    title: e.title,
    description: 'Thí nghiệm IoT (ESP32/cảm biến) — mở tại Lab để đo & phân tích dữ liệu thật.',
    lop: e.lop, chuong: e.chuong, bai: e.bai,
    url: LabService.fullUrl(e),
  }
}

export const SimulationLibrary = {
  /** Danh sách mô phỏng phù hợp 1 bài (canvas chuyên biệt + GeoGebra). */
  getSimsByLesson(lop: number, chuong: number, bai: number): SimEntry[] {
    const bespoke = SIM_REGISTRY.filter((s) => s.lop === lop && s.chuong === chuong && s.bai === bai)
    let ggb: SimEntry[] = []
    let lab: SimEntry[] = []
    try {
      ggb = (GeoGebraService.getByLesson(lop, chuong, bai) || []).map(ggbToEntry)
    } catch { ggb = [] }
    try {
      lab = (LabService.getByLesson(lop, chuong, bai) || []).map(labToEntry)
    } catch { lab = [] }
    return [...bespoke, ...ggb, ...lab]
  },

  getAll(): SimEntry[] {
    return [...SIM_REGISTRY]
  },

  /** Chuyển 1 SimEntry → block nội dung cho InteractiveContentRenderer. */
  toContentBlock(e: SimEntry): Record<string, any> {
    if (e.kind === 'geogebra') {
      return { type: 'simulation', simulationType: 'geogebra', ggbFile: e.ggbFile, title: e.title, description: e.description, height: 520 }
    }
    if (e.kind === 'video') {
      return { type: 'video_simulation', videoPath: e.videoPath, title: e.title, description: e.description }
    }
    if (e.kind === 'lab') {
      return { type: 'text', content: `🧪 **Thí nghiệm Lab IoT:** [${e.title}](${e.url}) — mở tại Lab để đo bằng cảm biến ESP32 và phân tích dữ liệu.` }
    }
    return { type: e.type, title: e.title, description: e.description, ...(e.params || {}) }
  },

  /** Chuyển → thẻ cho lesson-builder (tái dùng các loại thẻ sẵn có). */
  toLessonCard(e: SimEntry): Record<string, any> {
    if (e.kind === 'geogebra') {
      return { type: 'simulation', kind: 'geogebra', ggbFile: e.ggbFile, title: e.title, height: 520 }
    }
    if (e.kind === 'video') {
      return { type: 'video', url: e.videoPath, title: e.title }
    }
    if (e.kind === 'lab') {
      // Thẻ 'rich': tiêu đề + nút/link mở thí nghiệm Lab (render qua ContentRenderer).
      return {
        type: 'rich',
        title: `🧪 Thí nghiệm: ${e.title}`,
        items: [
          { type: 'heading', content: `🧪 Thí nghiệm Lab IoT: ${e.title}`, level: 3 },
          { type: 'text', content: `Làm thí nghiệm thật với cảm biến ESP32 và phân tích dữ liệu: [Mở thí nghiệm tại Lab ↗](${e.url})` },
        ],
      }
    }
    // Mô phỏng canvas → bọc trong thẻ 'rich' chứa block tương tác (render qua InteractiveContentRenderer).
    return { type: 'rich', title: e.title, items: [this.toContentBlock(e)] }
  },
}

export default SimulationLibrary
